import React, { useState } from 'react'
import {
  useFloating,
  offset,
  flip,
  shift,
  useClick,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
  Placement,
  safePolygon,
  ElementProps,
} from '@floating-ui/react'
import styled from '@emotion/styled'

export interface PopoverButtonProps {
  trigger: React.ReactNode | (({ open }: { open: boolean }) => React.ReactNode)
  children: React.ReactNode
  config?: PopoverConfig
  // to control the open state externally
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  blurConfig?: BlurConfig
}

type PopoverConfig = {
  offset?: number
  placement?: Placement
  openingMode?: 'hover' | 'click'
  x?: number
  y?: number
  closeOnClick?: boolean
}

type BlurConfig = {
  isBlurEnabled?: boolean
  blurStyles?: React.CSSProperties
}

const defaultConfig: Omit<Required<PopoverConfig>, 'x' | 'y'> = {
  offset: 8,
  placement: 'bottom-start',
  openingMode: 'click',
  closeOnClick: false,
}

const PopoverButton: React.FC<PopoverButtonProps> = ({
  trigger,
  children,
  config,
  isOpen,
  onOpenChange,
  blurConfig,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)

  const open = isOpen ?? uncontrolledOpen
  const setOpen = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen)
    } else {
      setUncontrolledOpen(newOpen)
    }
  }

  const mergedConfig = { ...defaultConfig, ...config }

  const { x, y, refs, strategy, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: mergedConfig.placement,
    middleware: [offset(mergedConfig.offset), flip(), shift()],
  })

  const hover =
    mergedConfig.openingMode === 'hover' &&
    useHover(context, {
      handleClose: safePolygon({
        requireIntent: false,
      }),
      delay: { open: 0, close: 100 },
    })
  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context)

  const interactions = [mergedConfig.openingMode === 'hover' ? hover : undefined, click, dismiss, role].filter(
    (interaction): interaction is ElementProps => interaction !== undefined
  )

  const { getReferenceProps, getFloatingProps } = useInteractions(interactions)

  const handleChildrenClick = () => mergedConfig.closeOnClick && setOpen(false)
  return (
    <>
      <button ref={refs.setReference} {...getReferenceProps()}>
        {typeof trigger === 'function' ? trigger({ open }) : trigger}
      </button>
      {blurConfig?.isBlurEnabled && <Backdrop isOpen={open} style={blurConfig.blurStyles} />}
      {open && (
        <ChildrenWrapper
          ref={refs.setFloating}
          {...getFloatingProps()}
          strategy={strategy}
          x={mergedConfig.x ?? x}
          y={mergedConfig.y ?? y}
          onClick={handleChildrenClick}
        >
          {children}
        </ChildrenWrapper>
      )}
    </>
  )
}

interface FloatingProps {
  strategy: React.CSSProperties['position']
  x: number | null
  y: number | null
}

const ChildrenWrapper = styled.div<FloatingProps>`
  position: ${({ strategy }) => strategy};
  top: ${({ y }) => (y !== null ? `${y}px` : '0')};
  left: ${({ x }) => (x !== null ? `${x}px` : '0')};
  z-index: var(--zIndex-modal);
  width: 100%;
`

const Backdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--neutral-700);
  opacity: ${({ isOpen }) => (isOpen ? 0.9 : 0)};
  transition: opacity 0.1s ease;
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  z-index: calc(var(--zIndex-modal));
`

export default PopoverButton
